import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { Box, Container, Tab, Tabs } from '@mui/material';
import { Alert } from '@mui/material';
import Page from '../Page';
import Header from './Header';
import WorkoutDetailsEditor from './WorkoutDetailsEditor';
import WorkoutAnalytics from './WorkoutAnalytics';
import WorkoutContent from './WorkoutContent';
import WorkoutContentV1 from './WorkoutContentV1';
import WorkoutPreview from './WorkoutPreview';
import Loading from '../../components/shared/Loading';
import { useMutation, useQuery } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import {
  DUPLICATE_WORKOUT_MUTATION,
  UPDATE_WORKOUT_MUTATION,
  GET_WORKOUT_QUERY,
} from '../../graphql/workout';
import config from '../../config';
import useWorkoutAutosave from '../../hooks/useWorkoutAutosave';

const prepareWorkout = (workout) => {
  const copy = _.cloneDeep(workout);
  if (copy) {
    copy.coverImages = copy.coverImageUrl
      ? [{ url: copy.coverImageUrl, aspect: 'square' }]
      : [];
  }
  return copy;
};

const tabs = [
  { value: 'details', label: 'Overview' },
  { value: 'content', label: 'Workout' },
  { value: 'preview', label: 'Preview' },
  { value: 'analytics', label: 'Analytics' },
];

const WorkoutEditor = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('content');
  const [workout, setWorkout] = useState(null);
  const [previewRefresh, setPreviewRefresh] = useState(1);
  const { enqueueSnackbar } = useSnackbar();

  let useGroupsEditor = true;
  // TODO remove WorkoutContentV1 WorkoutExerciseItemV1 as well once everyone likes this editor
  // useGroupsEditor = queryParams.useGroupsEditor === 'true';
  // turn off for katie's firebase user id
  // useGroupsEditor = !['k3x4CLj9mzMtr5xB7uF1wtY9vgz2'].includes(user.uid);

  useEffect(() => {
    if (params.tab) {
      setCurrentTab(params.tab);
    }
  }, [params]);

  const id = params.id;
  const [updateWorkout] = useMutation(UPDATE_WORKOUT_MUTATION);
  const [duplicateWorkout, { loading: duplicateWorkoutLoading }] = useMutation(
    DUPLICATE_WORKOUT_MUTATION,
    {
      onCompleted: (data) => {
        const duplicatedWorkoutId = data?.duplicateWorkout?._id;
        if (duplicatedWorkoutId) {
          enqueueSnackbar('Workout duplicated.', { autoHideDuration: 2000 });
          navigate(`/trainer/workouts/${duplicatedWorkoutId}`);
        }
      },
      onError: (error) => {
        enqueueSnackbar(`Duplicating failed: ${error.message}`, {
          autoHideDuration: 3000,
        });
      },
    }
  );

  const { loading, error, data } = useQuery(GET_WORKOUT_QUERY, {
    variables: { id },
  });

  useEffect(() => {
    if (data?.workout) {
      // Apollo also updates this query after mutations. Keep local edits intact.
      setWorkout((current) =>
        current?._id === data.workout._id
          ? current
          : prepareWorkout(data.workout)
      );
    }
  }, [data]);

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
    navigate(`/trainer/workouts/${workout._id}/${value}`);
  };

  const persistWorkout = async (workout) => {
    const mapExerciseToInput = (i) => {
      const o = {
        _id: i._id,
        exerciseId: i.exerciseId,
        duration: i.duration,
        isTimed: i.isTimed,
        reps: i.reps,
        sets: i.sets,
        weight: i.weight,
        weightUnit: i.weightUnit,
        percentOf1RM: i.percentOf1RM,
        restBetweenSetsDuration: i.restBetweenSetsDuration,
        restAfterExerciseDuration: i.restAfterExerciseDuration,
        groupName: i.groupName,
        notes: i.notes,
      };
      if (!o._id) {
        delete o._id;
      }
      return o;
    };

    const input = {
      title: workout.title,
      desc: workout.desc,
      tags: workout.tags,
      status: workout.status,
      coverImages:
        workout.coverImages != null && !!workout.coverImages.length
          ? workout.coverImages
          : [],
      workoutType: workout.workoutType,
      rounds: workout.rounds,
      restBetweenRounds: workout.restBetweenRounds,
      level: workout.level,
      youtubeVideoUrl: workout.youtubeVideoUrl,
      videos: workout.videos,

      // TODO, use exerciseList as Input don't do this, make sure exerciseList is saved
      exerciseList: workout.exercises?.map(mapExerciseToInput),
    };

    if (useGroupsEditor) {
      input.exerciseGroups = workout.exerciseGroups?.map((group) => {
        const groupCopy = _.cloneDeep(group);
        groupCopy.exerciseList =
          group?.exercises?.map(mapExerciseToInput) || [];
        delete groupCopy.exercises; // exerciseList is used for input, exercises is only for read
        delete groupCopy._id;
        return groupCopy;
      });
    }

    const result = await updateWorkout({
      variables: { id: workout._id, input },
    });
    if (!result.data?.updateWorkout)
      throw new Error('No saved workout returned');
    const savedWorkout = prepareWorkout(result.data.updateWorkout);
    // Group IDs are omitted from the mutation input. Retain UI identities so
    // autosaving does not remount rows or lose the selected circuit.
    savedWorkout.exerciseGroups = savedWorkout.exerciseGroups?.map(
      (group, index) => ({
        ...group,
        _id: workout.exerciseGroups?.[index]?._id ?? group._id,
      })
    );
    return savedWorkout;
  };

  const { changeWorkout, saveWorkout } = useWorkoutAutosave({
    workout,
    setWorkout,
    persist: persistWorkout,
    onSaved: () => {
      setPreviewRefresh((current) => current + 1);
      enqueueSnackbar('Workout saved.', { autoHideDuration: 2000 });
    },
    onError: () =>
      enqueueSnackbar(
        'Saving failed! Your edits are still here; use Save to retry.',
        {
          variant: 'error',
          autoHideDuration: 5000,
        }
      ),
  });

  const handleDuplicate = () => {
    if (!workout?._id || duplicateWorkoutLoading) {
      return;
    }
    enqueueSnackbar('Duplicating...', { autoHideDuration: 2000 });
    duplicateWorkout({ variables: { id: workout._id } });
  };

  if (error) {
    return (
      <Container maxWidth={false}>
        <Alert severity="error">
          {error.message || 'Unable to load workout.'}
        </Alert>
      </Container>
    );
  }
  if (loading || !workout) {
    return <Loading />;
  }

  return (
    <Page title={`Workout Editor : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Header
          workout={workout}
          onDuplicate={handleDuplicate}
          onPreview={() => setCurrentTab('preview')}
          onSave={() => saveWorkout(workout)}
        />
        <Box mt={3} sx={{ borderBottom: '1px solid #dbe3ee' }}>
          <Tabs
            onChange={handleTabsChange}
            scrollButtons="auto"
            value={currentTab}
            variant="scrollable"
            textColor="secondary"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Box>
        <Box pb={4} mt={2}>
          {currentTab === 'details' && (
            <WorkoutDetailsEditor
              workout={workout}
              setWorkout={changeWorkout}
              saveWorkout={saveWorkout}
            />
          )}

          {currentTab === 'content' && !useGroupsEditor && (
            <WorkoutContentV1
              workout={workout}
              setWorkout={changeWorkout}
              saveWorkout={saveWorkout}
            />
          )}
          {currentTab === 'content' && !!useGroupsEditor && (
            <WorkoutContent
              workout={workout}
              setWorkout={changeWorkout}
              saveWorkout={saveWorkout}
            />
          )}

          {currentTab === 'preview' && (
            <WorkoutPreview key={previewRefresh} workout={workout} />
          )}
          {currentTab === 'analytics' && <WorkoutAnalytics workout={workout} />}
        </Box>
      </Container>
    </Page>
  );
};

export default WorkoutEditor;
